/**
 * DocBits Notification System - Main Service
 *
 * Centralized, type-safe notification service for consistent
 * toast notifications across the DocBits application.
 *
 * Features:
 * - Type-safe API with TypeScript
 * - Consistent styling and positioning
 * - Notification history tracking
 * - Notification grouping (batch operations)
 * - Analytics tracking (optional)
 * - Accessibility support (ARIA labels)
 * - Testing helpers (mock mode)
 *
 * @module DocBitsNotification
 *
 * @example
 * // Simple success notification
 * DocBitsNotification.success('Changes saved successfully');
 *
 * @example
 * // Error notification with action button
 * DocBitsNotification.error('Failed to save', {
 *   caption: 'Please try again',
 *   actions: [{
 *     label: 'Retry',
 *     handler: () => saveData()
 *   }]
 * });
 *
 * @example
 * // Custom notification with full control
 * DocBitsNotification.custom({
 *   type: 'custom',
 *   message: 'Processing...',
 *   color: 'purple',
 *   icon: 'sync',
 *   duration: 'persistent'
 * });
 */

import { notify, type NotifyOptions } from 'src/components/notifications/notify';
import type {
  NotificationOptions,
  SuccessNotificationOptions,
  ErrorNotificationOptions,
  WarningNotificationOptions,
  InfoNotificationOptions,
  CustomNotificationOptions,
  NotificationDismiss,
  NotificationHistoryEntry,
  NotificationGroup,
  DocBitsNotificationConfig,
  NotificationTestOptions,
} from './types';
import {
  DURATION_CONFIG,
  DEFAULT_CONFIG,
  ANALYTICS_EVENT_PREFIX,
  STORAGE_KEYS,
} from './constants';

/**
 * DocBits Notification Service
 *
 * Provides static methods for showing notifications with consistent
 * styling, behavior, and tracking across the application.
 */
export class DocBitsNotification {
  /** Service configuration */
  private static config: DocBitsNotificationConfig = { ...DEFAULT_CONFIG };

  /** Notification history */
  private static history: NotificationHistoryEntry[] = [];

  /** Active notification groups */
  private static groups: Map<string, NotificationGroup> = new Map();

  /** Testing configuration */
  private static testOptions: NotificationTestOptions = {
    mockMode: false,
    captureNotifications: false,
    testDismissDelay: 0,
  };

  /** Captured notifications for testing */
  private static capturedNotifications: NotificationOptions[] = [];

  /** Active notification dismiss functions */
  private static activeNotifications: Set<NotificationDismiss> = new Set();

  /**
   * Configure the notification service
   *
   * @param config - Partial configuration to merge with defaults
   */
  static configure(config: Partial<DocBitsNotificationConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Get current configuration
   */
  static getConfig(): DocBitsNotificationConfig {
    return { ...this.config };
  }

  /**
   * Show success notification
   *
   * @param message - Success message to display
   * @param options - Optional configuration
   * @returns Dismiss function
   *
   * @example
   * DocBitsNotification.success('Document saved');
   * DocBitsNotification.success('Upload complete', { duration: 3000 });
   */
  static success(
    message: string,
    options?: Omit<SuccessNotificationOptions, 'message'>,
  ): NotificationDismiss {
    return this.show({
      type: 'success',
      message,
      ...options,
    } as SuccessNotificationOptions);
  }

  /**
   * Show error notification
   *
   * @param message - Error message to display
   * @param options - Optional configuration
   * @returns Dismiss function
   *
   * @example
   * DocBitsNotification.error('Failed to save document');
   * DocBitsNotification.error('Network error', {
   *   caption: 'Please check your connection',
   *   error: new Error('Connection timeout')
   * });
   */
  static error(
    message: string,
    options?: Omit<ErrorNotificationOptions, 'message'>,
  ): NotificationDismiss {
    // Log error to console if enabled and error object provided
    if (options?.logToConsole !== false && options?.error) {
      console.error('[DocBits Notification]', message, options.error);
    }

    return this.show({
      type: 'error',
      message,
      ...options,
    } as ErrorNotificationOptions);
  }

  /**
   * Show warning notification
   *
   * @param message - Warning message to display
   * @param options - Optional configuration
   * @returns Dismiss function
   *
   * @example
   * DocBitsNotification.warning('Unsaved changes');
   * DocBitsNotification.warning('Session expiring soon', {
   *   actions: [{
   *     label: 'Extend',
   *     handler: () => extendSession()
   *   }]
   * });
   */
  static warning(
    message: string,
    options?: Omit<WarningNotificationOptions, 'message'>,
  ): NotificationDismiss {
    return this.show({
      type: 'warning',
      message,
      ...options,
    } as WarningNotificationOptions);
  }

  /**
   * Show info notification
   *
   * @param message - Info message to display
   * @param options - Optional configuration
   * @returns Dismiss function
   *
   * @example
   * DocBitsNotification.info('New version available');
   * DocBitsNotification.info('Processing in background', { duration: 5000 });
   */
  static info(
    message: string,
    options?: Omit<InfoNotificationOptions, 'message'>,
  ): NotificationDismiss {
    return this.show({
      type: 'info',
      message,
      ...options,
    } as InfoNotificationOptions);
  }

  /**
   * Show custom notification with full control
   *
   * @param options - Full notification configuration
   * @returns Dismiss function
   *
   * @example
   * DocBitsNotification.custom({
   *   type: 'custom',
   *   message: 'Custom notification',
   *   color: 'purple',
   *   icon: 'star',
   *   position: 'center'
   * });
   */
  static custom(options: CustomNotificationOptions): NotificationDismiss {
    return this.show(options);
  }

  /**
   * Core notification show method
   *
   * @private
   * @param options - Notification options
   * @returns Dismiss function
   */
  private static show(options: NotificationOptions): NotificationDismiss {
    // Handle testing mode
    if (this.testOptions.mockMode || this.testOptions.captureNotifications) {
      this.capturedNotifications.push(options);
      if (this.testOptions.mockMode) {
        // Return mock dismiss function
        return () => {};
      }
    }

    // Check for notification grouping
    if (this.config.enableGrouping && options.groupId) {
      return this.handleGroupedNotification(options);
    }

    // Calculate duration
    const duration = this.calculateDuration(options);

    const toasterType = options.type === 'custom' ? 'info' : options.type || 'info';

    const toasterOptions: NotifyOptions = { duration };
    if (options.caption) toasterOptions.description = options.caption;

    const primaryAction = options.actions?.[0];
    if (primaryAction) {
      toasterOptions.action = {
        label: primaryAction.label,
        onClick: () => primaryAction.handler(),
      };
    }

    const id = notify[toasterType](options.message, toasterOptions);

    const wrappedDismiss: NotificationDismiss = () => {
      this.activeNotifications.delete(wrappedDismiss);
      notify.dismiss(id);
    };
    this.activeNotifications.add(wrappedDismiss);

    // Track in history
    if (this.config.enableHistory) {
      this.addToHistory(options, duration);
    }

    // Track analytics
    if (this.config.enableAnalytics && options.analyticsEvent) {
      this.trackAnalytics(options.analyticsEvent, options);
    }

    return wrappedDismiss;
  }

  /**
   * Handle grouped notifications
   *
   * @private
   */
  private static handleGroupedNotification(
    options: NotificationOptions,
  ): NotificationDismiss {
    const groupId = options.groupId!;
    const existingGroup = this.groups.get(groupId);

    if (existingGroup) {
      // Update existing group
      existingGroup.count++;
      existingGroup.lastMessage = options.message;
      existingGroup.lastTimestamp = new Date();

      // Dismiss old notification
      existingGroup.dismiss();

      // Show updated group notification
      const groupOptions = {
        ...options,
        message: `${options.message} (${existingGroup.count} total)`,
      };

      const dismiss = this.show({ ...groupOptions, groupId: undefined });

      // Update group dismiss function
      existingGroup.dismiss = dismiss;

      return dismiss;
    } else {
      // Create new group
      const dismiss = this.show({ ...options, groupId: undefined });

      this.groups.set(groupId, {
        id: groupId,
        count: 1,
        lastMessage: options.message,
        lastTimestamp: new Date(),
        dismiss,
      });

      // Auto-cleanup group after window expires
      setTimeout(() => {
        this.groups.delete(groupId);
      }, this.config.groupingWindowMs || DEFAULT_CONFIG.groupingWindowMs!);

      return dismiss;
    }
  }

  /**
   * Calculate notification duration
   *
   * @private
   */
  private static calculateDuration(options: NotificationOptions): number {
    const configuredDuration = options.duration;

    // Handle explicit durations
    if (configuredDuration === 'persistent') {
      return DURATION_CONFIG.PERSISTENT;
    }

    if (typeof configuredDuration === 'number') {
      return configuredDuration;
    }

    // Auto-calculate based on message length
    const messageLength = options.message.length + (options.caption?.length || 0);
    const calculated =
      messageLength * (this.config.durationPerCharacter || DURATION_CONFIG.MS_PER_CHARACTER);

    // Clamp to min/max
    const min = this.config.minDuration || DURATION_CONFIG.MIN_DURATION;
    const max = this.config.maxDuration || DURATION_CONFIG.MAX_DURATION;

    return Math.max(min, Math.min(max, calculated));
  }

  /**
   * Add notification to history
   *
   * @private
   */
  private static addToHistory(options: NotificationOptions, duration: number): void {
    const entry: NotificationHistoryEntry = {
      id: this.generateId(),
      type: options.type || 'info',
      message: options.message,
      caption: options.caption,
      timestamp: new Date(),
      dismissed: false,
      durationShown: duration,
      groupId: options.groupId,
      analyticsEvent: options.analyticsEvent,
    };

    this.history.push(entry);

    // Trim history to max size
    const maxSize = this.config.maxHistorySize || DEFAULT_CONFIG.maxHistorySize!;
    if (this.history.length > maxSize) {
      this.history = this.history.slice(-maxSize);
    }

    // Persist to localStorage if enabled
    if (typeof localStorage !== 'undefined') {
      try {
        localStorage.setItem(STORAGE_KEYS.HISTORY, JSON.stringify(this.history));
      } catch (err) {
        console.warn('[DocBits Notification] Failed to persist history:', err);
      }
    }
  }

  /**
   * Track analytics event
   *
   * @private
   */
  private static trackAnalytics(eventName: string, options: NotificationOptions): void {
    const fullEventName = `${ANALYTICS_EVENT_PREFIX}_${eventName}`;

    // Placeholder for analytics integration
    // Replace with your analytics service (e.g., Google Analytics, Mixpanel)
    if (typeof window !== 'undefined' && 'gtag' in window) {
      // Google Analytics example
      (window as any).gtag('event', fullEventName, {
        notification_type: options.type,
        notification_message: options.message,
      });
    }

    console.debug('[DocBits Notification Analytics]', fullEventName, options);
  }

  /**
   * Generate unique notification ID
   *
   * @private
   */
  private static generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
  }

  /**
   * Get notification history
   *
   * @returns Array of history entries
   */
  static getHistory(): NotificationHistoryEntry[] {
    return [...this.history];
  }

  /**
   * Clear notification history
   */
  static clearHistory(): void {
    this.history = [];
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEYS.HISTORY);
    }
  }

  /**
   * Get active notification groups
   *
   * @returns Array of active groups
   */
  static getGroups(): NotificationGroup[] {
    return Array.from(this.groups.values());
  }

  /**
   * Clear all notification groups
   */
  static clearGroups(): void {
    this.groups.forEach((group) => group.dismiss());
    this.groups.clear();
  }

  /**
   * Dismiss all active notifications
   *
   * @example
   * // Dismiss all notifications when navigating away
   * DocBitsNotification.dismissAll();
   */
  static dismissAll(): void {
    this.activeNotifications.forEach((dismiss) => {
      try {
        dismiss();
      } catch (e) {
        // Notification may already be dismissed
      }
    });
    this.activeNotifications.clear();
    this.clearGroups();
  }

  /**
   * Configure testing options
   *
   * @param options - Testing configuration
   *
   * @example
   * // Enable mock mode for unit tests
   * DocBitsNotification.setTestOptions({ mockMode: true });
   */
  static setTestOptions(options: NotificationTestOptions): void {
    this.testOptions = { ...this.testOptions, ...options };
  }

  /**
   * Get captured notifications (for testing)
   *
   * @returns Array of captured notification options
   */
  static getCapturedNotifications(): NotificationOptions[] {
    return [...this.capturedNotifications];
  }

  /**
   * Clear captured notifications
   */
  static clearCapturedNotifications(): void {
    this.capturedNotifications = [];
  }

  /**
   * Reset service to default state (for testing)
   */
  static reset(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.history = [];
    this.groups.clear();
    this.activeNotifications.clear();
    this.testOptions = {
      mockMode: false,
      captureNotifications: false,
      testDismissDelay: 0,
    };
    this.capturedNotifications = [];
  }
}

// Export default instance
export default DocBitsNotification;
